<template>
  <div class="calendario-root" data-aos="fade-right">
    <!-- ── Barra de controles: navegación + filtros + contadores ── -->
    <BarraControles :maquinas="catMaquinas" @programar-recurrente="abrirRecurrente" />

    <!-- ── Calendario a pantalla completa ── -->
    <div class="calendario-bloque">
      <CalendarioVista
        ref="vistaRef"
        @click-dia="onClickDia"
        @click-evento="onClickEvento"
      />
    </div>

    <!-- ── Modales ── -->
    <ModalCrearEvento
      ref="modalCrear"
      :fecha-seleccionada="fechaSeleccionada"
      :maquinas="catMaquinas"
      :rutinas="catRutinas"
      @creado="store.cargarMes()"
    />

    <ModalProgramarRecurrente
      ref="modalRecurrente"
      :maquinas="catMaquinas"
      :rutinas="catRutinas"
      @completado="store.cargarMes()"
    />

    <ModalDetalleEvento ref="modalDetalle" :evento="eventoSeleccionado" />
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { getMaquinas, getRutinas } from "@/utils/api_backend";
import { useCalendarioStore } from "@/store/calendarioStore";
import Swal from "sweetalert2";

import BarraControles from "./BarraControles.vue";
import CalendarioVista from "./CalendarioVista.vue";
import ModalCrearEvento from "./ModalCrearEvento.vue";
import ModalProgramarRecurrente from "./ModalProgramarRecurrente.vue";
import ModalDetalleEvento from "./ModalDetalleEvento.vue";

const store = useCalendarioStore();

// ── Catálogos ─────────────────────────────────────────────────
const catMaquinas = ref([]);
const catRutinas = ref([]);

// ── Selección ─────────────────────────────────────────────────
const fechaSeleccionada = ref("");
const eventoSeleccionado = ref(null);

// ── Refs componentes ──────────────────────────────────────────
const vistaRef = ref(null);
const modalCrear = ref(null);
const modalRecurrente = ref(null);
const modalDetalle = ref(null);

// ── Helpers ───────────────────────────────────────────────────

/**
 * Verifica si la fecha pertenece al mes actualmente visible en el calendario.
 * Evita que los días "fantasma" de meses adyacentes en FullCalendar abran el modal.
 */
function esFechaDelMesActual(fechaStr) {
  if (!store.mesActual || !fechaStr) return false;
  // fechaStr viene en formato "YYYY-MM-DD"
  const [anio, mes] = fechaStr.split("-");
  const [mesAnio, mesMes] = store.mesActual.split("-");
  return anio === mesAnio && mes === mesMes;
}

// ── Handlers ──────────────────────────────────────────────────

/**
 * Click en un día del calendario.
 *
 * REGLA DE NEGOCIO:
 *  - Si el día no pertenece al mes actual → ignorar (días fantasma).
 *  - Cualquier día válido abre el modal de crear, sin importar cuántos
 *    eventos ya existan ese día: es completamente válido tener varias
 *    rutinas en la misma fecha para distintas máquinas.
 *  - La validación de duplicado real (misma máquina + rutina + período)
 *    la resuelve el backend devolviendo CONFLICTO_PERIODO.
 */
function onClickDia(fechaStr) {
  if (!esFechaDelMesActual(fechaStr)) return;
  fechaSeleccionada.value = fechaStr;
  modalCrear.value?.abrir();
}

/**
 * Click en un evento existente del calendario.
 *
 * REGLA DE NEGOCIO:
 *  - Cualquier estatus → abrir modal de detalle (solo lectura).
 *  - El modal de detalle internamente muestra u oculta acciones según el estatus.
 *  - Ya NO hay botón de reprogramar actividades (se movió a Mtto. Preventivo).
 */
function onClickEvento(evento) {
  eventoSeleccionado.value = evento;
  modalDetalle.value?.abrir();
}

function abrirRecurrente() {
  modalRecurrente.value?.abrir();
}

// ── Sidebar toggle → redibujar FullCalendar ───────────────────
function onSidebarToggle() {
  setTimeout(() => {
    vistaRef.value?.updateSize();
  }, 320);
}

// ── Init ──────────────────────────────────────────────────────
onMounted(async () => {
  store.inicializar();
  try {
    const [maq, rut] = await Promise.all([getMaquinas(), getRutinas()]);
    catMaquinas.value = maq;
    catRutinas.value = rut;
    await store.cargarMes();
  } catch {
    Swal.fire({
      icon: "error",
      title: "Error de conexión",
      text: "No se pudieron cargar los datos del calendario.",
      confirmButtonColor: "var(--d-brown)",
    });
  }
});

defineExpose({ onSidebarToggle });
</script>

<style scoped>
.calendario-root {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: 100%;
}

.calendario-bloque {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  overflow: hidden;
}

@media (max-width: 991.98px) {
  /* En móvil el calendario debe ocupar todo el ancho, sin el margen
     lateral de 1rem de .main-content — igual que la barra de arriba. */
  .calendario-bloque {
    margin: 0 -1rem;
    border-radius: 0;
  }
}
</style>
